import { useCallback, useState } from 'react'
import log from 'loglevel'
import {
  BulkCreateDocumentRequestsRequest,
  CountDocumentRequestsResponse,
  CreateAndLinkRequestAttestationRequest,
  CreateAndLinkRequestPolicyDocRequest,
  CreateOrUpdateDocumentRequestRequest,
  DeleteDocumentRequestRequest,
  DeleteRequestAttestationRequest,
  DeleteRequestCommentRequest,
  DeleteRequestDocumentRequest,
  DeleteRequestPolicyDocRequest,
  DOCUMENT_REQUEST_STATUS,
  DocumentRequest,
  DocumentRequests,
  LinkControlsToRequestRequest,
  LinkPoliciesToRequestRequest,
  LinkRequestsToControlRequest,
  LinkRisksToRequestRequest,
  LinkVendorsToRequestRequest,
  ListDocumentRequestsRequest,
  ListRequestAttestationsRequest,
  ListRequestCommentsRequest,
  ListRequestCommentsResponse,
  MarkRequestCommentsAsReadRequest,
  ListRequestDocumentsRequest,
  ListRequestPolicyDocsRequest,
  REQUEST_USE_CASE,
  RequestFinding,
  RequestComment,
  RequestDocument,
  RequestDocuments,
  RequestPolicyDocs,
  RequestStatusUpdate,
  UnlinkControlFromRequestRequest,
  UnlinkPolicyFromRequestRequest,
  UnlinkRiskFromRequestRequest,
  UnlinkVendorFromRequestRequest,
  UpdateDocumentRequestRequest,
} from '@trustero/trustero-api-web/lib/request/request_pb'
import { RequestPromiseClient } from '@trustero/trustero-api-web/lib/request/request_grpc_web_pb'
import { AttestationPromiseClient } from '@trustero/trustero-api-web/lib/vendormanagement/attestation_grpc_web_pb'
import { Attestations } from '@trustero/trustero-api-web/lib/vendormanagement/attestation_pb'
import { VendorPromiseClient } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_grpc_web_pb'
import { GetVendorRequest } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import {
  Int32Value,
  StringValue,
} from 'google-protobuf/google/protobuf/wrappers_pb'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { StringList } from '@trustero/trustero-api-web/lib/common/collections_pb'
import { toast } from 'react-toastify'
import { UserPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { ListControlsRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import { PolicyPromiseClient } from '@trustero/trustero-api-web/lib/model/policy_grpc_web_pb'
import { GetPolicyRequest } from '@trustero/trustero-api-web/lib/model/policy_pb'
import {
  OeControlGap,
  OeControlTestResult,
  ObjectGapRecommendations,
  ReportType,
} from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import {
  MODEL_TYPE,
  FileUpload,
} from '@trustero/trustero-api-web/lib/common/model_pb'
import { EvidenceGroupId } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { NONE_ID } from 'src/Utils/globalConstants'
import { getOeTestTitle } from 'src/Utils/helpers/string.helpers'
import { useInvalidateSubscriptionCache } from 'src/global.hooks/notification.hooks'
import { useSwrImmutableGrpc } from '../useSwrImmutableGrpc'
import { dateToTimestamp } from '../../../Utils/formatDate'
import { useAuthorizedGrpcClient } from '../../../adapter'
import { useInAudit } from '../../../context/AuditContext'
import { ImportRequestCSVRow } from '../../ModalForms/Requests/AddRequestBulkUpload/AddRequestBulkUpload.shared'
import { ToastPrompts } from '../../../Utils/helpers/toast'
import { useGrpcRevalidateByMethod } from '../useGrpcMutate'
import { useListMember } from '../hooks/useListMember'
import { GrpcResponse, NewGrpcResponse } from '../hooks/types'
import { useNewModel } from '../hooks/useNewModel'

export const useInvalidateDocumentRequestsCache = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    await Promise.all([
      mutateFunc(RequestPromiseClient.prototype.listDocumentRequests),
      mutateFunc(RequestPromiseClient.prototype.countDocumentRequests),
      mutateFunc(ModelPromiseClient.prototype.listControls),
    ])
  }, [mutateFunc])
}

type UseAddOrUpdateDocumentRequestParams = {
  id?: string
  request?: string
  controlIds?: string[]
  dueDate?: Date
  status?: number
  ownerId?: string
  unlinkControlId?: string
}

type UpdateDocumentRequestParams = {
  id?: string
  request?: string
  description?: string
  dueDate?: Date
  status?: DOCUMENT_REQUEST_STATUS
  ownerId?: string
  useCaseType?: REQUEST_USE_CASE
}

// Deprecated
export const useAddOrUpdateDocumentRequest = (): (({
  id,
  request,
  controlIds,
  dueDate,
  status,
  ownerId,
  unlinkControlId,
}: UseAddOrUpdateDocumentRequestParams) => Promise<DocumentRequest>) => {
  const mutator = useInvalidateDocumentRequestsCache()
  const subscriptionMutator = useInvalidateSubscriptionCache()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)
  const { auditId } = useInAudit()

  return async ({
    id,
    request,
    controlIds,
    dueDate,
    status,
    ownerId,
    unlinkControlId,
  }: UseAddOrUpdateDocumentRequestParams): Promise<DocumentRequest> => {
    const createOrUpdateRequest = new CreateOrUpdateDocumentRequestRequest()

    if (id) {
      createOrUpdateRequest.setId(new StringValue().setValue(id))
    }
    if (request) {
      createOrUpdateRequest.setRequest(new StringValue().setValue(request))
    }
    if (auditId) {
      createOrUpdateRequest.setAuditId(new StringValue().setValue(auditId))
    }
    if (controlIds) {
      createOrUpdateRequest.setControlIds(
        new StringList().setItemList(controlIds),
      )
    }
    if (dueDate) {
      const dueDateTimestamp = dateToTimestamp(dueDate)
      createOrUpdateRequest.setDueDate(dueDateTimestamp)
    }
    // check if status is undefined, because 0 is falsey
    if (status !== undefined) {
      createOrUpdateRequest.setStatus(new Int32Value().setValue(status))
    }
    if (ownerId) {
      if (ownerId === NONE_ID) {
        createOrUpdateRequest.setOwnerId(new StringValue().setValue(''))
      } else {
        createOrUpdateRequest.setOwnerId(new StringValue().setValue(ownerId))
      }
    }
    if (unlinkControlId) {
      createOrUpdateRequest.setUnlinkControlId(
        new StringValue().setValue(unlinkControlId),
      )
    }

    let response = new DocumentRequest()
    try {
      response = await requestClient.createOrUpdateDocumentRequest(
        createOrUpdateRequest,
      )
      await mutator()
      await subscriptionMutator()
    } catch (e) {
      log.error(
        'error creating document request in useAddDocumentRequest hook',
        e,
      )
      if (controlIds || unlinkControlId) {
        toast(ToastPrompts.LINK_CONTROL_TO_REQUEST)
      }
    }
    return response
  }
}

export const useAddDocumentRequest = (): (({
  request,
}: {
  request: DocumentRequest
}) => Promise<void>) => {
  const mutator = useInvalidateDocumentRequestsCache()
  const subscriptionMutator = useInvalidateSubscriptionCache()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)
  const { auditId } = useInAudit()

  return async ({ request }: { request: DocumentRequest }): Promise<void> => {
    // auditId is optional — requests can exist outside an audit (CC mode)
    if (auditId) {
      request.setAuditId(auditId)
    }
    try {
      await requestClient.createDocumentRequest(request)
      await mutator()
      await subscriptionMutator()
    } catch (e) {
      log.error(
        'error creating document request in useAddDocumentRequest hook',
        e,
      )
    }
    return
  }
}

export const useUpdateDocumentRequest = (): (({
  id,
  request,
  description,
  dueDate,
  status,
  ownerId,
}: UpdateDocumentRequestParams) => Promise<void>) => {
  const mutator = useInvalidateDocumentRequestsCache()
  const subscriptionMutator = useInvalidateSubscriptionCache()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({
    id,
    request,
    description,
    dueDate,
    status,
    ownerId,
    useCaseType,
  }: UpdateDocumentRequestParams): Promise<void> => {
    const updateRequest = new UpdateDocumentRequestRequest()

    if (id) {
      updateRequest.setId(new StringValue().setValue(id))
    }
    if (request) {
      updateRequest.setRequest(new StringValue().setValue(request))
    }
    if (description !== undefined) {
      updateRequest.setDescription(new StringValue().setValue(description))
    }
    if (dueDate) {
      const dueDateTimestamp = Timestamp.fromDate(dueDate)
      updateRequest.setDueDate(dueDateTimestamp)
    }
    if (status !== undefined) {
      updateRequest.setStatus(new RequestStatusUpdate().setNewStatus(status))
    }
    if (ownerId) {
      if (ownerId === NONE_ID) {
        updateRequest.setOwnerId(new StringValue().setValue(''))
      } else {
        updateRequest.setOwnerId(new StringValue().setValue(ownerId))
      }
    }
    if (useCaseType !== undefined) {
      updateRequest.setUseCaseType(new Int32Value().setValue(useCaseType))
    }

    try {
      await requestClient.updateDocumentRequest(updateRequest)
      await mutator()
      await subscriptionMutator()
    } catch (e) {
      log.error(
        'error updating document request in useUpdateDocumentRequest hook',
        e,
      )
    }
    return
  }
}

export const useLinkControlsToRequest = (): (({
  id,
  controlIds,
}: {
  id: string
  controlIds: string[]
}) => Promise<void>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({
    id,
    controlIds,
  }: {
    id: string
    controlIds: string[]
  }): Promise<void> => {
    const updateRequest = new LinkControlsToRequestRequest()
    updateRequest.setRequestId(id)
    updateRequest.setControlIdsList(controlIds)

    try {
      await requestClient.linkControlsToRequest(updateRequest)
      await mutator(ModelPromiseClient.prototype.listControls)
    } catch (e) {
      log.error(
        'error updating request controls in useUpdateRequestControls hook',
        e,
      )
    }
  }
}

export const useLinkRequestsToControl = (): (({
  id,
  requestIds,
}: {
  id: string
  requestIds: string[]
}) => Promise<void>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({
    id,
    requestIds,
  }: {
    id: string
    requestIds: string[]
  }): Promise<void> => {
    const updateRequest = new LinkRequestsToControlRequest()
    updateRequest.setControlId(id)
    updateRequest.setRequestIdsList(requestIds)

    try {
      await requestClient.linkRequestsToControl(updateRequest)
      await mutator(RequestPromiseClient.prototype.listDocumentRequests)
      await mutator(AttachmentPromiseClient.prototype.getControlEvidenceTabView)
      await mutator(ModelPromiseClient.prototype.getControl)
    } catch (e) {
      log.error(
        'error updating request controls in useUpdateRequestControls hook',
        e,
      )
    }
  }
}

export const useUnlinkControlsFromRequest = (): (({
  id,
  controlId,
}: {
  id: string
  controlId: string
}) => Promise<void>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({
    id,
    controlId,
  }: {
    id: string
    controlId: string
  }): Promise<void> => {
    const updateRequest = new UnlinkControlFromRequestRequest()
    updateRequest.setRequestId(id)
    updateRequest.setControlId(controlId)

    try {
      await requestClient.unlinkControlFromRequest(updateRequest)
      await mutator(RequestPromiseClient.prototype.listDocumentRequests)
    } catch (e) {
      log.error(
        'error unlinking controls from request in useUnlinkControlsFromRequest hook',
        e,
      )
    }
  }
}

export const useLinkPoliciesToRequest = (): (({
  requestId,
  policyModelIds,
}: {
  requestId: string
  policyModelIds: string[]
}) => Promise<void>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({
    requestId,
    policyModelIds,
  }: {
    requestId: string
    policyModelIds: string[]
  }): Promise<void> => {
    const req = new LinkPoliciesToRequestRequest()
    req.setRequestId(requestId)
    req.setPolicyModelIdsList(policyModelIds)
    try {
      await requestClient.linkPoliciesToRequest(req)
      await mutator(RequestPromiseClient.prototype.listDocumentRequests)
    } catch (e) {
      log.error('error linking policies to request', e)
    }
  }
}

export const useUnlinkPolicyFromRequest = (): (({
  requestId,
  policyModelId,
}: {
  requestId: string
  policyModelId: string
}) => Promise<void>) => {
  const invalidateCache = useInvalidateDocumentRequestsCache()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({
    requestId,
    policyModelId,
  }: {
    requestId: string
    policyModelId: string
  }): Promise<void> => {
    const req = new UnlinkPolicyFromRequestRequest()
    req.setRequestId(requestId)
    req.setPolicyModelId(policyModelId)
    try {
      await requestClient.unlinkPolicyFromRequest(req)
      await invalidateCache()
    } catch (e) {
      log.error('error unlinking policy from request', e)
    }
  }
}

export const useLinkVendorsToRequest = (): (({
  requestId,
  vendorIds,
}: {
  requestId: string
  vendorIds: string[]
}) => Promise<void>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({
    requestId,
    vendorIds,
  }: {
    requestId: string
    vendorIds: string[]
  }): Promise<void> => {
    const req = new LinkVendorsToRequestRequest()
    req.setRequestId(requestId)
    req.setVendorIdsList(vendorIds)
    try {
      await requestClient.linkVendorsToRequest(req)
      await mutator(RequestPromiseClient.prototype.listDocumentRequests)
    } catch (e) {
      log.error('error linking vendors to request', e)
    }
  }
}

export const useUnlinkVendorFromRequest = (): (({
  requestId,
  vendorId,
}: {
  requestId: string
  vendorId: string
}) => Promise<void>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({
    requestId,
    vendorId,
  }: {
    requestId: string
    vendorId: string
  }): Promise<void> => {
    const req = new UnlinkVendorFromRequestRequest()
    req.setRequestId(requestId)
    req.setVendorId(vendorId)
    try {
      await requestClient.unlinkVendorFromRequest(req)
      await mutator(RequestPromiseClient.prototype.listDocumentRequests)
    } catch (e) {
      log.error('error unlinking vendor from request', e)
    }
  }
}

export const useLinkRisksToRequest = (): (({
  requestId,
  riskIds,
}: {
  requestId: string
  riskIds: string[]
}) => Promise<void>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({
    requestId,
    riskIds,
  }: {
    requestId: string
    riskIds: string[]
  }): Promise<void> => {
    const req = new LinkRisksToRequestRequest()
    req.setRequestId(requestId)
    req.setRiskIdsList(riskIds)
    try {
      await requestClient.linkRisksToRequest(req)
      await mutator(RequestPromiseClient.prototype.listDocumentRequests)
    } catch (e) {
      log.error('error linking risks to request', e)
    }
  }
}

export const useUnlinkRiskFromRequest = (): (({
  requestId,
  riskId,
}: {
  requestId: string
  riskId: string
}) => Promise<void>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({
    requestId,
    riskId,
  }: {
    requestId: string
    riskId: string
  }): Promise<void> => {
    const req = new UnlinkRiskFromRequestRequest()
    req.setRequestId(requestId)
    req.setRiskId(riskId)
    try {
      await requestClient.unlinkRiskFromRequest(req)
      await mutator(RequestPromiseClient.prototype.listDocumentRequests)
    } catch (e) {
      log.error('error unlinking risk from request', e)
    }
  }
}

export const useBulkRequestUpload = ({
  allowEmptyAuditId = false,
}: {
  allowEmptyAuditId?: boolean
} = {}): ((
  requestsData: ImportRequestCSVRow[],
  auditIdOverride?: string,
  ownerId?: string,
) => Promise<void>) => {
  const mutator = useInvalidateDocumentRequestsCache()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)
  const { auditId } = useInAudit()

  return useCallback(
    async (
      requestsData: ImportRequestCSVRow[],
      auditIdOverride?: string,
      ownerId?: string,
    ): Promise<void> => {
      try {
        const effectiveAuditId =
          allowEmptyAuditId && auditIdOverride !== undefined
            ? auditIdOverride
            : auditIdOverride || auditId
        const uploadRequests = requestsData.map(
          ({ name, description, dueDate, controlIds }: ImportRequestCSVRow) => {
            const uploadRequest = new DocumentRequest()

            if (effectiveAuditId) {
              uploadRequest.setAuditId(effectiveAuditId)
            }
            uploadRequest.setName(name)
            uploadRequest.setUseCaseType(REQUEST_USE_CASE.REQUEST_FOR_EVIDENCE)
            if (description) {
              uploadRequest.setDescription(description)
            }
            const dueDateTimestamp = dateToTimestamp(dueDate)
            dueDate && uploadRequest.setDueDate(dueDateTimestamp)
            uploadRequest.setControlIdsList(
              controlIds.filter((model_id) => !!model_id),
            )
            if (ownerId) {
              uploadRequest.setOwnerId(ownerId)
            }
            return uploadRequest
          },
        )
        const req = new BulkCreateDocumentRequestsRequest().setRequests(
          new DocumentRequests().setItemsList(uploadRequests),
        )
        await requestClient.bulkCreateDocumentRequests(req)
      } catch (error) {
        log.error('error when making bulk request upload via CSV', error)
        toast('Upload failed. Please check formatting and try again.')
      }

      mutator()
      return
    },
    [allowEmptyAuditId, auditId, mutator, requestClient],
  )
}

export const useDocumentRequest = (
  id: string,
  shouldFetch = true,
): GrpcResponse<DocumentRequest> => {
  const { auditId } = useInAudit()
  const newModel = useNewModel()
  const getRequest = new ListDocumentRequestsRequest()
  auditId && getRequest.setAuditId(new StringValue().setValue(auditId))
  getRequest.setUseNewModel(newModel)
  const response = useListMember(
    {
      id,
      asyncCall: RequestPromiseClient.prototype.listDocumentRequests,
      request: getRequest,
      newCollection: () => new DocumentRequests(),
    },
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useDocumentRequests = ({
  request,
  controlId,
  policyModelId,
  vendorId,
  riskId,
  evidenceGroupId,
  evidenceInstanceId,
  shouldFetch = true,
}: {
  request: ListDocumentRequestsRequest
  controlId?: string
  policyModelId?: string
  vendorId?: string
  riskId?: string
  evidenceGroupId?: EvidenceGroupId
  evidenceInstanceId?: string
  shouldFetch?: boolean
}): GrpcResponse<DocumentRequests> => {
  const newModel = useNewModel()
  const { auditId } = useInAudit()
  controlId && request.setControlId(new StringValue().setValue(controlId))
  policyModelId &&
    request.setPolicyModelId(new StringValue().setValue(policyModelId))
  vendorId && request.setVendorId(new StringValue().setValue(vendorId))
  riskId && request.setRiskId(new StringValue().setValue(riskId))
  auditId && request.setAuditId(new StringValue().setValue(auditId))
  evidenceGroupId && request.setEvidenceGroupId(evidenceGroupId)
  evidenceInstanceId &&
    request.setEvidenceInstanceId(
      new StringValue().setValue(evidenceInstanceId),
    )
  request.setUseNewModel(newModel)
  const { response } = useSwrImmutableGrpc(
    RequestPromiseClient.prototype.listDocumentRequests,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useDeleteDocumentRequest = (id: string): (() => Promise<void>) => {
  const mutator = useInvalidateDocumentRequestsCache()
  const { auditId } = useInAudit()
  const deleteRequest = new DeleteDocumentRequestRequest().setId(id)
  if (auditId) {
    deleteRequest.setAuditId(auditId)
  }
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)
  return async () => {
    try {
      await requestClient.deleteDocumentRequest(deleteRequest)
      await mutator()
    } catch (e) {
      log.error(
        'error deleting document request in useDeleteDocumentRequest hook',
        e,
      )
    }
  }
}

export const useDocumentRequestCount = (
  auditId: string,
  shouldFetch = true,
): GrpcResponse<CountDocumentRequestsResponse> => {
  const newModel = useNewModel()
  const countRequest = new ListDocumentRequestsRequest()
    .setAuditId(new StringValue().setValue(auditId))
    .setUseNewModel(newModel)
  const { response } = useSwrImmutableGrpc(
    RequestPromiseClient.prototype.countDocumentRequests,
    countRequest,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useListRequestPolicyDocs = (
  requestId: string,
): GrpcResponse<RequestPolicyDocs> => {
  const req = new ListRequestPolicyDocsRequest()
  req.setRequestId(requestId)
  const { response } = useSwrImmutableGrpc(
    RequestPromiseClient.prototype.listRequestPolicyDocs,
    req,
    !!requestId,
  )
  return NewGrpcResponse(response)
}

export const useCreateAndLinkRequestPolicyDoc = (): (({
  requestId,
  policyModelId,
  caption,
  mime,
  content,
}: {
  requestId: string
  policyModelId: string
  caption: string
  mime: string
  content: Uint8Array
}) => Promise<string>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({
    requestId,
    policyModelId,
    caption,
    mime,
    content,
  }: {
    requestId: string
    policyModelId: string
    caption: string
    mime: string
    content: Uint8Array
  }): Promise<string> => {
    const req = new CreateAndLinkRequestPolicyDocRequest()
    req.setRequestId(requestId)
    req.setPolicyModelId(policyModelId)
    req.setCaption(caption)
    req.setMime(mime)
    req.setContent(content)
    try {
      const result = await requestClient.createAndLinkRequestPolicyDoc(req)
      await mutator(RequestPromiseClient.prototype.listRequestPolicyDocs)
      await mutator(
        AttachmentPromiseClient.prototype.getPolicyDocumentsByPolicyID,
      )
      return result.getContentId()
    } catch (e) {
      log.error('error creating and linking request policy doc', e)
      return ''
    }
  }
}

export const useDeleteRequestPolicyDoc = (): (({
  id,
}: {
  id: string
}) => Promise<void>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({ id }: { id: string }): Promise<void> => {
    const req = new DeleteRequestPolicyDocRequest()
    req.setId(id)
    try {
      await requestClient.deleteRequestPolicyDoc(req)
      await mutator(RequestPromiseClient.prototype.listRequestPolicyDocs)
      await mutator(RequestPromiseClient.prototype.listRequestComments)
    } catch (e) {
      log.error('error deleting request policy doc', e)
    }
  }
}

export const useListRequestAttestations = (
  requestId: string,
): GrpcResponse<Attestations> => {
  const req = new ListRequestAttestationsRequest()
  req.setRequestId(requestId)
  const { response } = useSwrImmutableGrpc(
    RequestPromiseClient.prototype.listRequestAttestations,
    req,
    !!requestId,
  )
  return NewGrpcResponse(response)
}

export const useCreateAndLinkRequestAttestation = (): (({
  requestId,
  vendorId,
  description,
  reportType,
  expirationDate,
  ownerEmail,
  mime,
  content,
}: {
  requestId: string
  vendorId: string
  description: string
  reportType: string
  expirationDate: Date
  ownerEmail: string
  mime: string
  content: Uint8Array
}) => Promise<string>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({
    requestId,
    vendorId,
    description,
    reportType,
    expirationDate,
    ownerEmail,
    mime,
    content,
  }: {
    requestId: string
    vendorId: string
    description: string
    reportType: string
    expirationDate: Date
    ownerEmail: string
    mime: string
    content: Uint8Array
  }): Promise<string> => {
    const req = new CreateAndLinkRequestAttestationRequest()
    req.setRequestId(requestId)
    req.setVendorId(vendorId)
    req.setDescription(description)
    req.setReportType(reportType)
    req.setExpirationDate(Timestamp.fromDate(expirationDate))
    req.setOwnerEmail(ownerEmail)
    req.setMime(mime)
    req.setContent(content)
    try {
      const result = await requestClient.createAndLinkRequestAttestation(req)
      await mutator(RequestPromiseClient.prototype.listRequestAttestations)
      await mutator(AttestationPromiseClient.prototype.listAttestations)
      return result.getContentId()
    } catch (e) {
      log.error('error creating and linking request attestation', e)
      return ''
    }
  }
}

export const useDeleteRequestAttestation = (): (({
  requestId,
  attestationId,
}: {
  requestId: string
  attestationId: string
}) => Promise<void>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({
    requestId,
    attestationId,
  }: {
    requestId: string
    attestationId: string
  }): Promise<void> => {
    const req = new DeleteRequestAttestationRequest()
    req.setRequestId(requestId)
    req.setAttestationId(attestationId)
    try {
      await requestClient.deleteRequestAttestation(req)
      await mutator(RequestPromiseClient.prototype.listRequestAttestations)
      await mutator(AttestationPromiseClient.prototype.listAttestations)
      await mutator(RequestPromiseClient.prototype.listRequestComments)
    } catch (e) {
      log.error('error deleting request attestation', e)
    }
  }
}

export const useListRequestDocuments = (
  requestId: string,
): GrpcResponse<RequestDocuments> => {
  const req = new ListRequestDocumentsRequest()
  req.setRequestId(requestId)
  const { response } = useSwrImmutableGrpc(
    RequestPromiseClient.prototype.listRequestDocuments,
    req,
    !!requestId,
  )
  return NewGrpcResponse(response)
}

export const useCreateRequestDocument = (): (({
  requestId,
  caption,
  mime,
  content,
}: {
  requestId: string
  caption: string
  mime: string
  content: Uint8Array
}) => Promise<string>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({
    requestId,
    caption,
    mime,
    content,
  }: {
    requestId: string
    caption: string
    mime: string
    content: Uint8Array
  }): Promise<string> => {
    const req = new RequestDocument()
    req.setRequestId(requestId)
    req.setCaption(caption)
    req.setMime(mime)
    req.setContent(content)
    try {
      const result = await requestClient.createRequestDocument(req)
      await mutator(RequestPromiseClient.prototype.listRequestDocuments)
      return result.getContentId()
    } catch (e) {
      log.error('error creating request document', e)
      return ''
    }
  }
}

export const useDeleteRequestDocument = (): (({
  id,
}: {
  id: string
}) => Promise<void>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({ id }: { id: string }): Promise<void> => {
    const req = new DeleteRequestDocumentRequest()
    req.setId(id)
    try {
      await requestClient.deleteRequestDocument(req)
      await mutator(RequestPromiseClient.prototype.listRequestDocuments)
      await mutator(RequestPromiseClient.prototype.listRequestComments)
    } catch (e) {
      log.error('error deleting request document', e)
    }
  }
}

export const controlTestSource = (artifactId: string): string =>
  JSON.stringify({ type: 'control_test', id: artifactId })

export const reportArtifactSource = (artifactId: string): string =>
  JSON.stringify({ type: 'pda_report', id: artifactId })

export const playbookReportSource = (artifactId: string): string =>
  JSON.stringify({ type: 'playbook_report', id: artifactId })

const objectTypeToUseCase = (objectType: MODEL_TYPE): REQUEST_USE_CASE => {
  switch (objectType) {
    case MODEL_TYPE.POLICY:
    case MODEL_TYPE.CONTROL:
    case MODEL_TYPE.VENDOR:
      return REQUEST_USE_CASE.REQUEST_FOR_ACTION
    default:
      return REQUEST_USE_CASE.REQUEST_FOR_INFORMATION
  }
}

const linkObjectToRequest = (
  req: DocumentRequest,
  objectId: string,
  objectType: MODEL_TYPE,
): void => {
  if (!objectId) return
  switch (objectType) {
    case MODEL_TYPE.POLICY:
      req.setPolicyModelIdsList([objectId])
      break
    case MODEL_TYPE.CONTROL:
      req.setControlIdsList([objectId])
      break
    case MODEL_TYPE.VENDOR:
      req.setVendorIdsList([objectId])
      break
  }
}

async function fetchObjectOwnerEmail(
  policyClient: PolicyPromiseClient,
  modelClient: ModelPromiseClient,
  vendorClient: VendorPromiseClient,
  ogr: ObjectGapRecommendations,
): Promise<string> {
  const objectId = ogr.getObjectId()
  if (!objectId) return ''
  switch (ogr.getObjectType()) {
    case MODEL_TYPE.POLICY: {
      const policy = await policyClient.get(
        new GetPolicyRequest().setId(objectId),
      )
      return policy.getOwnerEmail()
    }
    case MODEL_TYPE.CONTROL: {
      const controls = await modelClient.listControls(
        new ListControlsRequest()
          .setControlIdsList([objectId])
          .setUseNewModel(true),
      )
      return controls.getItemsList()[0]?.getOwnerEmail() ?? ''
    }
    case MODEL_TYPE.VENDOR: {
      const vendor = await vendorClient.getVendor(
        new GetVendorRequest().setId(objectId),
      )
      return vendor.getOwnerEmail()
    }
    default:
      return ''
  }
}

const nameForGap = (objectName: string, reportType: number): string => {
  switch (reportType) {
    case ReportType.REPORT_PDA:
      return `PDA Gap for ${objectName}`
    default:
      return `Gap remediation – ${objectName}`
  }
}

const descriptionForReportType = (reportType: number): string => {
  switch (reportType) {
    case ReportType.REPORT_PDA:
      return 'Address gaps from policy design assessment.'
    default:
      return 'Gaps identified during AI assessment. See findings for details.'
  }
}

export const useBulkCreateRequestsFromArtifactGaps = (
  artifactId: string,
  gapRecommendations: ObjectGapRecommendations[],
  reportType: number,
): { createRequests: () => Promise<void>; isCreating: boolean } => {
  const [isCreating, setIsCreating] = useState(false)
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)
  const policyClient = useAuthorizedGrpcClient(PolicyPromiseClient)
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const vendorClient = useAuthorizedGrpcClient(VendorPromiseClient)
  const userClient = useAuthorizedGrpcClient(UserPromiseClient)
  const mutator = useInvalidateDocumentRequestsCache()

  const createRequests = useCallback(async () => {
    setIsCreating(true)
    try {
      // Build email→userId map for owner resolution
      const usersResp = await userClient.getUsersInAccount(new Empty())
      const emailToId = new Map<string, string>()
      usersResp
        .getUsersList()
        .forEach((u) => emailToId.set(u.getEmail(), u.getId()))

      // Resolve owner IDs for each gap recommendation (failures are non-fatal)
      const ownerIdByObjectId = new Map<string, string>()
      await Promise.allSettled(
        gapRecommendations.map(async (ogr) => {
          const objectId = ogr.getObjectId()
          if (!objectId) return
          const ownerEmail = await fetchObjectOwnerEmail(
            policyClient,
            modelClient,
            vendorClient,
            ogr,
          )
          const ownerId = emailToId.get(ownerEmail)
          if (ownerId) ownerIdByObjectId.set(objectId, ownerId)
        }),
      )

      const requests = gapRecommendations.map((ogr) => {
        const req = new DocumentRequest()
        req.setName(
          ogr.getName() || nameForGap(ogr.getObjectName(), reportType),
        )
        req.setDescription(
          ogr.getDescription() || descriptionForReportType(reportType),
        )
        req.setUseCaseType(objectTypeToUseCase(ogr.getObjectType()))
        req.setSource(
          reportType === ReportType.REPORT_PLAYBOOK_SUMMARY
            ? playbookReportSource(artifactId)
            : reportArtifactSource(artifactId),
        )
        linkObjectToRequest(req, ogr.getObjectId(), ogr.getObjectType())
        const findings = ogr.getGapsList().map((gap) => {
          const finding = new RequestFinding()
          finding.setFinding(gap.getGap())
          finding.setRecommendation(gap.getRecommendation())
          return finding
        })
        req.setFindingsList(findings)
        const ownerId = ownerIdByObjectId.get(ogr.getObjectId())
        if (ownerId) req.setOwnerId(ownerId)
        return req
      })

      const bulkReq = new BulkCreateDocumentRequestsRequest().setRequests(
        new DocumentRequests().setItemsList(requests),
      )
      await requestClient.bulkCreateDocumentRequests(bulkReq)
      mutator()
    } catch (error) {
      log.error(
        'useBulkCreateRequestsFromArtifactGaps: error creating requests',
        error,
      )
      throw error
    } finally {
      setIsCreating(false)
    }
  }, [
    artifactId,
    gapRecommendations,
    modelClient,
    mutator,
    policyClient,
    reportType,
    requestClient,
    userClient,
    vendorClient,
  ])

  return { createRequests, isCreating }
}

export const useBulkCreateRequestsFromOeArtifact = (
  controlId: string,
  controlModelId: string,
  artifactId: string,
  testResultsWithGaps: OeControlTestResult[],
  controlOwnerId?: string,
  legacyGaps?: OeControlGap[],
): { createRequests: () => Promise<void>; isCreating: boolean } => {
  const [isCreating, setIsCreating] = useState(false)
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)
  const mutator = useInvalidateDocumentRequestsCache()
  const { auditId } = useInAudit()

  const createRequests = useCallback(async () => {
    setIsCreating(true)
    try {
      const buildBase = (req: DocumentRequest): DocumentRequest => {
        req.setDescription('See details in Findings and Recommendations')
        req.setUseCaseType(REQUEST_USE_CASE.REQUEST_FOR_EVIDENCE)
        req.setControlIdsList([controlId])
        req.setSource(controlTestSource(artifactId))
        if (auditId) req.setAuditId(auditId)
        if (controlOwnerId) req.setOwnerId(controlOwnerId)
        return req
      }

      let requests: DocumentRequest[]

      if (legacyGaps && legacyGaps.length > 0) {
        // Old format: artifact-level OeControlGap with singular remediation string.
        // Create one request for the control with all gaps as findings.
        const req = buildBase(new DocumentRequest())
        req.setName(`Address failures in ${controlModelId}`)
        const findings = legacyGaps.map((gap) => {
          const finding = new RequestFinding()
          finding.setFinding(gap.getDescription())
          finding.setRecommendation(gap.getRemediation())
          return finding
        })
        req.setFindingsList(findings)
        requests = [req]
      } else {
        // New format: per-test OeControlTestGaps with repeated remediations.
        // Create one request per failing test result.
        requests = testResultsWithGaps.map((testResult) => {
          const req = buildBase(new DocumentRequest())
          req.setName(
            `Address failures in ${controlModelId}: ${getOeTestTitle(
              testResult.getTestString(),
            )}`,
          )
          const findings = testResult.getGapsList().map((gap) => {
            const finding = new RequestFinding()
            finding.setFinding(gap.getDescription())
            const remediations = gap.getRemediationsList()
            finding.setRecommendation(
              remediations.length > 0 ? `- ${remediations.join('\n- ')}` : '',
            )
            return finding
          })
          req.setFindingsList(findings)
          return req
        })
      }

      const bulkReq = new BulkCreateDocumentRequestsRequest().setRequests(
        new DocumentRequests().setItemsList(requests),
      )
      await requestClient.bulkCreateDocumentRequests(bulkReq)
      mutator()
    } catch (error) {
      log.error(
        'useBulkCreateRequestsFromOeArtifact: error creating requests',
        error,
      )
      throw error
    } finally {
      setIsCreating(false)
    }
  }, [
    artifactId,
    auditId,
    controlId,
    controlModelId,
    controlOwnerId,
    legacyGaps,
    mutator,
    requestClient,
    testResultsWithGaps,
  ])

  return { createRequests, isCreating }
}

export const useListRequestComments = (
  requestId: string,
): GrpcResponse<ListRequestCommentsResponse> => {
  const req = new ListRequestCommentsRequest()
  req.setRequestId(requestId)
  const { response } = useSwrImmutableGrpc(
    RequestPromiseClient.prototype.listRequestComments,
    req,
    !!requestId,
  )
  return NewGrpcResponse(response)
}

export const useCreateRequestComment = (): (({
  requestId,
  content,
  attachmentRefs,
}: {
  requestId: string
  content: string
  attachmentRefs?: Array<{
    contentId: string
    mimeType: string
    fileName: string
  }>
}) => Promise<RequestComment>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({
    requestId,
    content,
    attachmentRefs,
  }: {
    requestId: string
    content: string
    attachmentRefs?: Array<{
      contentId: string
      mimeType: string
      fileName: string
    }>
  }): Promise<RequestComment> => {
    const req = new RequestComment()
    req.setRequestId(requestId)
    req.setContent(content)
    if (attachmentRefs?.length) {
      const refs = attachmentRefs
        .filter(({ contentId }) => !!contentId)
        .map(({ contentId, mimeType, fileName }) =>
          new FileUpload()
            .setContentId(contentId)
            .setMimeType(mimeType)
            .setFileName(fileName),
        )
      req.setAttachmentRefsList(refs)
    }
    try {
      const response = await requestClient.createRequestComment(req)
      await mutator(RequestPromiseClient.prototype.listRequestComments)
      return response
    } catch (e) {
      log.error('error creating request comment', e)
      throw e
    }
  }
}

export const useDeleteRequestComment = (): (({
  id,
}: {
  id: string
}) => Promise<void>) => {
  const mutator = useGrpcRevalidateByMethod()
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)

  return async ({ id }: { id: string }): Promise<void> => {
    const req = new DeleteRequestCommentRequest()
    req.setId(id)
    try {
      await requestClient.deleteRequestComment(req)
      await mutator(RequestPromiseClient.prototype.listRequestComments)
    } catch (e) {
      log.error('error deleting request comment', e)
      throw e
    }
  }
}

export const useMarkRequestCommentsAsRead = (): (({
  requestId,
}: {
  requestId: string
}) => Promise<void>) => {
  const requestClient = useAuthorizedGrpcClient(RequestPromiseClient)
  const mutator = useInvalidateDocumentRequestsCache()

  return useCallback(
    async ({ requestId }: { requestId: string }): Promise<void> => {
      const req = new MarkRequestCommentsAsReadRequest()
      req.setRequestId(requestId)
      try {
        await requestClient.markRequestCommentsAsRead(req)
        await mutator()
      } catch (e) {
        log.error('error marking request comments as read', e)
      }
    },
    [requestClient, mutator],
  )
}
